import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";
import { setDofIstek, getDofIstekById } from "../../../api/kalite/dofApi";
import { getPersonel } from "../../../api/authApi";
import { ArrowLeft, Link2, ShieldCheck } from "lucide-react";
import Select from "react-select";
import DofWizardSteps from "./DofWizardSteps";

import "./Dof.css";
import {
  cleanDofPayload,
  toDateInputValue,
  ASSIGNMENT_LABELS,
  getMissingWorkflowRoleLabels,
  initials,
} from "./dofFormUtils";

// DÖF'ü açan kişi sadece uygunsuzluğu tanımlar ve kişileri atar.
// Kök neden / faaliyetleri Sorumlu, takip ve kapatmayı Takipçi detay sayfasından girer.
const CREATE_STEPS = [
  { step: 1, label: "DÖF Bilgileri" },
  { step: 2, label: "Uygunsuzluk ve Kişi Atama" },
];

function DofiWizard() {
  const { user, permissions = [] } = useAuth();
  // Kalite tam yetki: eski (kağıt) DÖF'leri geçmiş kayıt olarak girebilir
  const fullAccess = permissions.includes("dof_fullAccess");

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kaynakId = searchParams.get("kaynak");
  const [kaynakDof, setKaynakDof] = useState(null);
  const [personelLoading, setPersonelLoading] = useState(false);
  const [personelList, setPersonelList] = useState([]);

  const [assignedUserForm, setAssignedUserForm] = useState({
    userId: "",
    fullName: "",
    email: "",
    departman: "",
    gorev: "",
    assignmentType: "sorumlu",
  });

  const [formData, setFormData] = useState({
    dofNo: "",
    dofIstekTarih: "",
    faliyetTuru: "",
    tekrarUygunsuzluk: "",
    icDenetim: "",
    disDenetim: "",
    iyilestirme: "",
    icProsesSikayet: "",
    musteriSikayet: "",
    digerAciklama: "",
    uygunsuzlukTanimi: "",
    dofiTalepEden: "",
    kokNeden: "",
    duzetmeFaliyetTanimi: "",
    duzeltmeSorumlu: "",
    duzeltmeTerminSure: "",
    duzelticiFaliyetTanimi: "",
    duzelticiSorumlu: "",
    duzelticiTerminSure: "",
    onleyiciciFaliyetTanimi: "",
    onleyiciciSorumlu: "",
    onleyiciciTerminSure: "",
    takipTarihi: "",
    takibiGerceklestiren: "",
    takipSonucuOneri: "",
    prosedur: "",
    prosedurRevTarihi: "",
    prosedurRevNo: "",
    talimat: "",
    talimatRevTarihi: "",
    talimatRevNo: "",
    isAkisi: "",
    isAkisiRevTarihi: "",
    isAkisiRevNo: "",
    form: "",
    formRevTarihi: "",
    formRevNo: "",
    digerRevAciklama: "",
    digerRevTarihi: "",
    digerRevNo: "",
    yeniDofAcilmali: "",
    dofKapatmaTarihiOnay: "",
    yonetimTemsilcisi: "",
    dofIstenenBirim: "",
    assignedUsers: [],
  });

  useEffect(() => {
    const fetchPersonel = async () => {
      setPersonelLoading(true);

      try {
        const response = await getPersonel();

        const list =
          response?.data?.data ||
          response?.data?.results ||
          response?.data ||
          [];

        setPersonelList(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Personel listesi alınamadı:", error);
        setPersonelList([]);
      } finally {
        setPersonelLoading(false);
      }
    };

    fetchPersonel();
  }, []);

  // Etkin bulunmayan / yeni DÖF açılması gereken bir kayıttan yeni DÖF: bilgileri önceden doldur
  useEffect(() => {
    if (!kaynakId) return;
    const fetchKaynak = async () => {
      try {
        const response = await getDofIstekById(kaynakId);
        const src = response?.data?.data;
        if (!src) return;
        setKaynakDof(src);
        setFormData((prev) => ({
          ...prev,
          kaynakDofId: src._id,
          dofIstekTarih: new Date().toISOString().slice(0, 10),
          faliyetTuru: src.faliyetTuru || "",
          tekrarUygunsuzluk: true,
          icDenetim: src.icDenetim ?? "",
          disDenetim: src.disDenetim ?? "",
          iyilestirme: src.iyilestirme ?? "",
          icProsesSikayet: src.icProsesSikayet ?? "",
          musteriSikayet: src.musteriSikayet ?? "",
          digerAciklama: src.digerAciklama || "",
          uygunsuzlukTanimi: `${src.dofNo} numaralı DÖF'ün faaliyetleri etkin bulunmadı.\n\nÖnceki uygunsuzluk: ${src.uygunsuzlukTanimi || "-"}\nTakip sonucu: ${src.takipSonucuOneri || "-"}`,
          dofiTalepEden: src.dofiTalepEden || "",
          dofIstenenBirim: src.dofIstenenBirim || "",
          assignedUsers: (src.assignedUsers || []).map((u) => ({
            userId: u.userId,
            fullName: u.fullName,
            email: u.email,
            departman: u.departman,
            gorev: u.gorev,
            assignmentType: u.assignmentType,
          })),
        }));
      } catch (error) {
        console.error("Kaynak DÖF alınamadı:", error);
      }
    };
    fetchKaynak();
  }, [kaynakId]);

  if (!user) return null;

  const getPersonelId = (personel) => {
    return personel?._id || personel?.id || "";
  };

  const getPersonelFullName = (personel) => {
    return (
      personel?.fullName ||
      personel?.nameSurname ||
      personel?.nameSurnameText ||
      `${personel?.name || ""} ${personel?.surname || ""}`.trim() ||
      personel?.username ||
      ""
    );
  };

  const getPersonelDepartman = (personel) => {
    return (
      personel?.departman ||
      personel?.department ||
      personel?.departmentName ||
      personel?.birim ||
      personel?.unit ||
      ""
    );
  };

  const getPersonelGorev = (personel) => {
    return (
      personel?.gorev ||
      personel?.title ||
      personel?.position ||
      personel?.unvan ||
      personel?.roleName ||
      personel?.role?.name ||
      ""
    );
  };

  const getPersonelEmail = (personel) => {
    return String(personel?.email || "")
      .trim()
      .toLowerCase();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let parsedValue = value;

    if (value === "true") parsedValue = true;
    if (value === "false") parsedValue = false;

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handlePersonelSelect = (e) => {
    const selectedId = e.target.value;

    if (!selectedId) {
      setAssignedUserForm((prev) => ({
        ...prev,
        userId: "",
        fullName: "",
        email: "",
        departman: "",
        gorev: "",
      }));
      return;
    }

    const selectedPersonel = personelList.find(
      (item) => String(getPersonelId(item)) === String(selectedId)
    );

    if (!selectedPersonel) {
      alert("Seçilen personel bulunamadı.");
      return;
    }

    setAssignedUserForm((prev) => ({
      ...prev,
      userId: getPersonelId(selectedPersonel),
      fullName: getPersonelFullName(selectedPersonel),
      email: getPersonelEmail(selectedPersonel),
      departman: getPersonelDepartman(selectedPersonel),
      gorev: getPersonelGorev(selectedPersonel),
    }));
  };

  const addAssignedUser = () => {
    const userId = String(assignedUserForm.userId || "").trim();
    const fullName = String(assignedUserForm.fullName || "").trim();
    const email = String(assignedUserForm.email || "")
      .trim()
      .toLowerCase();
    const departman = String(assignedUserForm.departman || "").trim();
    const gorev = String(assignedUserForm.gorev || "").trim();
    const assignmentType = assignedUserForm.assignmentType || "sorumlu";

    if (!userId) {
      alert("Lütfen personel seçin.");
      return;
    }

    if (!fullName) {
      alert("Seçilen personelin ad soyad bilgisi bulunamadı.");
      return;
    }

    if (!email) {
      alert("Seçilen personelin e-posta bilgisi bulunamadı.");
      return;
    }

    const alreadyExists = formData.assignedUsers.some((item) => {
      const sameUserId = String(item.userId || "") === String(userId);
      const sameEmail =
        String(item.email || "")
          .trim()
          .toLowerCase() === email;

      return (sameUserId || sameEmail) && item.assignmentType === assignmentType;
    });

    if (alreadyExists) {
      alert("Bu kişi bu rolle zaten eklenmiş.");
      return;
    }

    const newAssignedUser = {
      userId,
      fullName,
      email,
      departman,
      gorev,
      assignmentType,
    };

    setFormData((prev) => ({
      ...prev,
      assignedUsers: [...(prev.assignedUsers || []), newAssignedUser],
    }));

    setAssignedUserForm({
      userId: "",
      fullName: "",
      email: "",
      departman: "",
      gorev: "",
      assignmentType: "sorumlu",
    });
  };

  const removeAssignedUser = (index) => {
    setFormData((prev) => ({
      ...prev,
      assignedUsers: prev.assignedUsers.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const nextStep = () => setCurrentStep((prev) => (prev < 2 ? prev + 1 : prev));
  const prevStep = () => setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));

  const resetForm = () => {
    setCurrentStep(1);

    setAssignedUserForm({
      userId: "",
      fullName: "",
      email: "",
      departman: "",
      gorev: "",
      assignmentType: "sorumlu",
    });

    setFormData({
      dofNo: "",
      dofIstekTarih: "",
      faliyetTuru: "",
      tekrarUygunsuzluk: "",
      icDenetim: "",
      disDenetim: "",
      iyilestirme: "",
      icProsesSikayet: "",
      musteriSikayet: "",
      digerAciklama: "",
      uygunsuzlukTanimi: "",
      dofiTalepEden: "",
      kokNeden: "",
      duzetmeFaliyetTanimi: "",
      duzeltmeSorumlu: "",
      duzeltmeTerminSure: "",
      duzelticiFaliyetTanimi: "",
      duzelticiSorumlu: "",
      duzelticiTerminSure: "",
      onleyiciciFaliyetTanimi: "",
      onleyiciciSorumlu: "",
      onleyiciciTerminSure: "",
      takipTarihi: "",
      takibiGerceklestiren: "",
      takipSonucuOneri: "",
      prosedur: "",
      prosedurRevTarihi: "",
      prosedurRevNo: "",
      talimat: "",
      talimatRevTarihi: "",
      talimatRevNo: "",
      isAkisi: "",
      isAkisiRevTarihi: "",
      isAkisiRevNo: "",
      form: "",
      formRevTarihi: "",
      formRevNo: "",
      digerRevAciklama: "",
      digerRevTarihi: "",
      digerRevNo: "",
      yeniDofAcilmali: "",
      dofKapatmaTarihiOnay: "",
      yonetimTemsilcisi: "",
      dofIstenenBirim: "",
      assignedUsers: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enter tuşu ile form erken gönderilmesin; son adıma kadar ilerlet
    if (currentStep < 2) {
      nextStep();
      return;
    }

    if (!formData.uygunsuzlukTanimi || !String(formData.uygunsuzlukTanimi).trim()) {
      alert("Lütfen uygunsuzluk tanımını doldurun.");
      setCurrentStep(2);
      return;
    }

    const gecmisKayit = fullAccess && formData.gecmisKayit === true;
    const missingRoles = getMissingWorkflowRoleLabels(formData.assignedUsers);
    if (missingRoles.length && !gecmisKayit) {
      alert(`İş akışını başlatmak için ${missingRoles.join(", ")} rolüne kişi atayın.`);
      setCurrentStep(2);
      return;
    }

    setLoading(true);

    try {
      const payload = cleanDofPayload(formData);
      if (gecmisKayit) {
        payload.gecmisKayit = true;
        payload.dofNo = String(formData.dofNo || "").trim();
      } else {
        delete payload.gecmisKayit;
      }
      const response = await setDofIstek(payload);

      if (response && response.data && response.data.success) {
        alert(
          gecmisKayit
            ? `${response.data.message || "DÖF kaydı oluşturuldu."} Şimdi kök neden, faaliyet, takip ve kapatma bilgilerini girip durumunu belirleyebilirsiniz.`
            : response.data.message || "DÖF Raporu başarıyla oluşturuldu."
        );
        const newId = response.data.data?._id;
        resetForm();
        if (newId) navigate(gecmisKayit ? `/DofEdit/${newId}` : `/DofDetay/${newId}`);
      } else {
        alert("Kaydetme işlemi sırasında bir sorun oluştu.");
      }
    } catch (error) {
      console.error("DÖF Kayıt Hatası:", error);
      alert(
        error.response?.data?.message ||
          "Sunucu ile iletişim kurulurken bir hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const fillMockData = () => {
    setFormData({
      dofNo: `DOF-${Math.floor(Math.random() * 10000)}`,
      dofIstekTarih: "2026-04-27",
      faliyetTuru: "duzeltici",
      tekrarUygunsuzluk: false,
      icDenetim: true,
      disDenetim: false,
      iyilestirme: true,
      icProsesSikayet: false,
      musteriSikayet: false,
      digerAciklama: "Test sürecinde tespit edilen ufak aksaklıklar",
      uygunsuzlukTanimi:
        "Üretim hattında X makinesinde oluşan basınç düşüşü nedeniyle hatalı üretim.",
      dofiTalepEden: "Kalite Kontrol / Büşra Salimoğlu",
      kokNeden:
        "Makine valfinde oluşan kireçlenme basınç düşüşüne sebep olmuştur.",
      duzetmeFaliyetTanimi:
        "Valfin acil olarak temizlenmesi ve basıncın normale döndürülmesi.",
      duzeltmeSorumlu: "Bakım Ekibi",
      duzeltmeTerminSure: "2026-04-28",
      duzelticiFaliyetTanimi:
        "Kireç önleyici filtre sisteminin hatta entegre edilmesi.",
      duzelticiSorumlu: "Mühendislik Departmanı",
      duzelticiTerminSure: "2026-05-15",
      onleyiciciFaliyetTanimi:
        "Aylık periyodik bakımlara valf kontrolü maddesinin eklenmesi.",
      onleyiciciSorumlu: "Planlama",
      onleyiciciTerminSure: "2026-05-01",
      takipTarihi: "2026-05-20",
      takibiGerceklestiren: "İrem Köse",
      takipSonucuOneri: "Filtre sistemi başarıyla kuruldu, basınç stabil.",
      prosedur: true,
      prosedurRevTarihi: "2026-05-05",
      prosedurRevNo: "REV-001",
      talimat: true,
      talimatRevTarihi: "2026-05-06",
      talimatRevNo: "TAL-002",
      isAkisi: false,
      isAkisiRevTarihi: "",
      isAkisiRevNo: "",
      form: true,
      formRevTarihi: "2026-05-10",
      formRevNo: "FRM-003",
      digerRevAciklama: "Kontrol listesi güncellendi",
      digerRevTarihi: "2026-05-11",
      digerRevNo: "LST-004",
      yeniDofAcilmali: false,
      dofKapatmaTarihiOnay: "2026-05-25",
      yonetimTemsilcisi: "Elif Taşlı",
      dofIstenenBirim: "Üretim Amiri",
      assignedUsers: [],
    });
  };

  return (
    <Layout>
      <div className="dof-page">
        <section className="dof-hero">
          <div>
            <span className="dof-kicker">
              <ShieldCheck size={15} /> Kalite · Yeni Kayıt
            </span>
            <h1>Yeni DÖF Aç</h1>
            <p>
              Siz sadece uygunsuzluğu tanımlayıp kişileri atarsınız. Sonrası otomatik ilerler: <b>Sorumlu</b> kök
              neden ve faaliyetleri girer → <b>Onaycı</b> onaylar → <b>Takipçi</b> etkinliği doğrulayıp kapatır. Her
              adımda ilgili kişiye mail gider. Sorumlu, Onaycı ve Takipçi ataması zorunludur; Bilgi rolü isteğe bağlıdır.
            </p>
          </div>
          <div className="dof-hero-actions">
            <button type="button" className="dof-btn dof-btn-ghost" onClick={() => navigate("/DofList")}>
              <ArrowLeft size={16} /> Liste
            </button>
            {process.env.NODE_ENV === "development" && (
              <button type="button" className="dof-btn dof-btn-ghost" onClick={fillMockData} disabled={loading}>
                Test Verisi
              </button>
            )}
          </div>
        </section>

        {kaynakDof && (
          <div className="dof-callout info">
            <Link2 size={17} />
            <div>
              Bu DÖF, <b>{kaynakDof.dofNo}</b> numaralı DÖF'ten açılıyor; bilgiler önceden dolduruldu.
            </div>
          </div>
        )}

        <section className="dof-panel dof-wizard">
          <DofWizardSteps currentStep={currentStep} onSelect={setCurrentStep} steps={CREATE_STEPS} />
          <div className="dof-wizard-body">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <div>
              <h5 className="mb-3">DÖF Bilgileri</h5>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">DÖF NO</label>
                  {fullAccess && formData.gecmisKayit === true ? (
                    <input
                      type="text"
                      name="dofNo"
                      className="form-control"
                      placeholder="Eski DÖF numarası (boş bırakılırsa otomatik verilir)"
                      value={formData.dofNo || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value="Kaydedildiğinde otomatik verilecek"
                      disabled
                      readOnly
                    />
                  )}
                  <span className="dof-hint">
                    Numara sistem tarafından sıralı verilir (ör. DOF-{new Date().getFullYear()}-0001).
                  </span>
                  {fullAccess && (
                    <label className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={formData.gecmisKayit === true}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setFormData((prev) => ({ ...prev, gecmisKayit: on, dofNo: on ? prev.dofNo : "" }));
                        }}
                      />
                      <span>
                        <b>Geçmiş (eski) DÖF kaydı</b> — mail gönderilmez, rol ataması zorunlu değildir; kaydettikten sonra tüm
                        alanları doldurup durumunu (onaylandı / kapalı) belirlersiniz.
                      </span>
                    </label>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">DÖF İstek Tarihi</label>
                  <input
                    type="date"
                    name="dofIstekTarih"
                    className="form-control"
                    value={toDateInputValue(formData.dofIstekTarih)}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Faaliyet Türü</label>
                  <div className="dof-tabs" style={{ maxWidth: 460 }}>
                    {[
                      ["duzeltici", "Düzeltici Faaliyet"],
                      ["onleyici", "Önleyici Faaliyet"],
                    ].map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={`dof-tab ${formData.faliyetTuru === value ? "active" : ""}`}
                        style={{ flex: 1, justifyContent: "center" }}
                        onClick={() => setFormData((prev) => ({ ...prev, faliyetTuru: value }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Tespit Türü</label>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {[
                    ["tekrarUygunsuzluk", "Tekrarlayan Uygunsuzluk"],
                    ["icDenetim", "İç Denetim"],
                    ["disDenetim", "Dış Denetim"],
                    ["iyilestirme", "İyileştirme"],
                    ["icProsesSikayet", "İç Proses Şikayeti"],
                    ["musteriSikayet", "Müşteri Şikayeti"],
                  ].map(([key, label]) => {
                    const on = formData[key] === true || formData[key] === "true";
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`dof-toggle ${on ? "on" : ""}`}
                        onClick={() => setFormData((prev) => ({ ...prev, [key]: !on }))}
                      >
                        <span className="dof-toggle-box">{on ? "✓" : ""}</span>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <span className="dof-hint">Birden fazla seçilebilir. Seçilmeyenler “Hayır” kabul edilir.</span>
              </div>

              <div className="mb-3">
                <label className="form-label">Diğer (Açıklama)</label>
                <input
                  type="text"
                  name="digerAciklama"
                  className="form-control"
                  placeholder="Listede olmayan bir tespit kaynağı varsa yazınız"
                  value={formData.digerAciklama || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                  disabled={loading}
                >
                  İleri
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h5 className="mb-3">Uygunsuzluk Tanımı</h5>

              <div className="row mb-3">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Uygunsuzluk Tanımı</label>
                  <textarea
                    name="uygunsuzlukTanimi"
                    className="form-control"
                    rows="3"
                    value={formData.uygunsuzlukTanimi}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    DÖF Talep Eden Kişi/Departman
                  </label>
                  <textarea
                    name="dofiTalepEden"
                    className="form-control"
                    rows="3"
                    value={formData.dofiTalepEden}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <h5 className="mb-3 border-top pt-3">
                    Sorumlu Kişi / Departman Ataması
                  </h5>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Personel Seç</label>
                      <Select
                        classNamePrefix="dof-select"
                        isClearable
                        isLoading={personelLoading}
                        isDisabled={personelLoading || loading}
                        placeholder={personelLoading ? "Personel listesi yükleniyor..." : "İsim, e-posta veya departman ile ara..."}
                        noOptionsMessage={() => "Personel bulunamadı"}
                        options={personelList.map((personel) => ({
                          value: String(getPersonelId(personel)),
                          label: getPersonelFullName(personel) || "İsimsiz Personel",
                          email: getPersonelEmail(personel),
                          departman: getPersonelDepartman(personel),
                        }))}
                        value={
                          assignedUserForm.userId
                            ? {
                                value: String(assignedUserForm.userId),
                                label: assignedUserForm.fullName || "Seçili personel",
                                email: assignedUserForm.email,
                                departman: assignedUserForm.departman,
                              }
                            : null
                        }
                        filterOption={(option, input) => {
                          const q = String(input || "").toLocaleLowerCase("tr-TR");
                          if (!q) return true;
                          return [option.data.label, option.data.email, option.data.departman]
                            .join(" ")
                            .toLocaleLowerCase("tr-TR")
                            .includes(q);
                        }}
                        formatOptionLabel={(option) => (
                          <div className="dof-person-option">
                            <strong>{option.label}</strong>
                            <small>{[option.departman, option.email].filter(Boolean).join(" · ")}</small>
                          </div>
                        )}
                        onChange={(option) => handlePersonelSelect({ target: { value: option?.value || "" } })}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Atama Tipi</label>
                      <div className="dof-tabs" style={{ width: "100%" }}>
                        {["sorumlu", "onayci", "takipci", "bilgi"].map((type) => (
                          <button
                            type="button"
                            key={type}
                            className={`dof-tab ${assignedUserForm.assignmentType === type ? "active" : ""}`}
                            style={{ flex: 1, justifyContent: "center" }}
                            onClick={() => setAssignedUserForm((prev) => ({ ...prev, assignmentType: type }))}
                          >
                            {ASSIGNMENT_LABELS[type]}
                          </button>
                        ))}
                      </div>
                      <span className="dof-hint">
                        {
                          {
                            sorumlu: "Kök neden ve faaliyetleri girip onaya gönderir.",
                            onayci: "Sorumlunun girdiği faaliyetleri onaylar / reddeder.",
                            takipci: "Onaydan sonra etkinliği doğrulayıp DÖF'ü kapatır.",
                            bilgi: "Sadece bilgilendirilir, işlem yapmaz.",
                          }[assignedUserForm.assignmentType]
                        }
                      </span>
                    </div>

                    <div className="col-md-12 text-end mb-3">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addAssignedUser}
                        disabled={loading || personelLoading || !assignedUserForm.userId}
                      >
                        + {ASSIGNMENT_LABELS[assignedUserForm.assignmentType]} Olarak Ekle
                      </button>
                    </div>
                  </div>

                  {(formData.assignedUsers || []).length > 0 ? (
                    <div className="dof-people" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
                      {(formData.assignedUsers || []).map((item, index) => (
                        <div
                          className="dof-person"
                          key={`${item.userId}-${item.email}-${item.assignmentType}-${index}`}
                          style={{ padding: 10, border: "1px solid var(--border)", borderRadius: 12 }}
                        >
                          <span className={`dof-avatar lg role-${item.assignmentType}`}>{initials(item.fullName)}</span>
                          <div>
                            <strong>{item.fullName || item.email}</strong>
                            <small>{[item.departman, item.email].filter(Boolean).join(" · ")}</small>
                          </div>
                          <span className={`dof-badge role-${item.assignmentType}`}>
                            {ASSIGNMENT_LABELS[item.assignmentType] || item.assignmentType}
                          </span>
                          <button
                            type="button"
                            className="dof-icon-btn danger"
                            title="Kaldır"
                            onClick={() => removeAssignedUser(index)}
                            disabled={loading}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dof-callout warn">
                      Henüz kimse atanmadı. En az birer <b>Sorumlu</b>, <b>Onaycı</b> ve <b>Takipçi</b> ekleyin.
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                  disabled={loading}
                >
                  Geri
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >
                  {loading
                    ? "Kaydediliyor..."
                    : fullAccess && formData.gecmisKayit === true
                    ? "Geçmiş DÖF'ü Kaydet ve Devam Et"
                    : "DÖF'ü Aç ve Bildirim Gönder"}
                </button>
              </div>
            </div>
          )}

        </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DofiWizard;
